// ./src/components/features/PlayListItemsTable/PlayListItemsTable.tsx
import React, { useState } from "react";
import {
  Text,
  Stack,
  Group,
  Badge,
  Table,
  Paper,
  ScrollArea,
  Center,
} from "@mantine/core";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import {
  AppTable,
  SortConfig,
  AppTableColumn,
  RecordActions,
  StatusBadge,
  AppIcon,
  LoadingIndicator,
} from "@/components/generic";
import { ContentTypeBadge } from "@/components/features";
import { PlayListItem } from "@/utils/api/PlayListItemService";
import {
  formatDuration,
  formatDate,
  formatDateTime,
  formatDaysOfWeek,
} from "@/utils/dateHelpers";

export interface PlayListItemsTableProps {
  /** Array of playlist items to display */
  playListItems: PlayListItem[];

  /** Loading state for initial load */
  loading: boolean;

  /** Loading state for loading more items */
  loadingMore: boolean;

  /** Whether there are more items to load */
  hasMore: boolean;

  /** Current sort configuration */
  sortBy: SortConfig[];

  /** Callback when sort changes */
  onSortChange: (sortBy: SortConfig[]) => void;

  /** Callback to load more items */
  onLoadMore: () => void;

  /** Callback when edit action is clicked */
  onEditPlayListItem?: (item: PlayListItem) => void;

  /** Callback when duplicate action is clicked */
  onDuplicatePlayListItem?: (item: PlayListItem) => void;

  /** Callback when remove action is clicked */
  onRemovePlayListItem?: (item: PlayListItem) => void;

  /** Callback when item order changes via drag and drop */
  onOrderChange?: (
    draggedItem: PlayListItem,
    itemBefore: PlayListItem | undefined,
    itemAfter: PlayListItem | undefined
  ) => Promise<void>;

  /** ID of playlist item currently being removed */
  removingPlayListItemId?: string | null;

  /** Table size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
}

export const PlayListItemsTable: React.FC<PlayListItemsTableProps> = ({
  playListItems,
  loading,
  loadingMore,
  hasMore,
  sortBy,
  onSortChange,
  onLoadMore,
  onEditPlayListItem,
  onDuplicatePlayListItem,
  onRemovePlayListItem,
  onOrderChange,
  removingPlayListItemId,
  size = "md",
}) => {
  const [reorderingItemId, setReorderingItemId] = useState<string | null>(null);
  const [optimisticItems, setOptimisticItems] = useState<PlayListItem[]>([]);

  // Use optimistic items if reordering, otherwise use props
  const displayItems = reorderingItemId ? optimisticItems : playListItems;
  const isDragDropEnabled = !!onOrderChange;

  // Helper to check if item has any overrides
  const hasOverrides = (item: PlayListItem): boolean => {
    return !!(
      item.durationSeconds ||
      item.startDatetime ||
      item.endDatetime ||
      item.startTime ||
      item.endTime ||
      item.recurrencePattern ||
      item.recurrenceDays
    );
  };

  // Helper to format time from UTC datetime string
  const formatTimeOnly = (timeString: string | undefined): string => {
    if (!timeString) return "";
    return formatDateTime(timeString, "time");
  };

  // Handle drag end
  const handleDragEnd = async (result: DropResult) => {
    if (!onOrderChange) return;

    const { source, destination } = result;

    // Dropped outside the list or no movement
    if (!destination || source.index === destination.index) {
      return;
    }

    const items = Array.from(playListItems);
    const [draggedItem] = items.splice(source.index, 1);
    items.splice(destination.index, 0, draggedItem);

    // Set optimistic state
    setOptimisticItems(items);
    setReorderingItemId(draggedItem.id || null);

    // Determine before and after items
    const itemBefore =
      destination.index > 0 ? items[destination.index - 1] : undefined;
    const itemAfter =
      destination.index < items.length - 1
        ? items[destination.index + 1]
        : undefined;

    try {
      // Call the async order change handler
      await onOrderChange(draggedItem, itemBefore, itemAfter);
    } catch (error) {
      console.error("Failed to reorder items:", error);
      // Reset to original order on error
      setOptimisticItems([]);
    } finally {
      setReorderingItemId(null);
    }
  };

  // Render overrides information
  const renderOverrides = (item: PlayListItem) => {
    if (!hasOverrides(item)) {
      return (
        <Text size="xs" c="dimmed">
          No overrides
        </Text>
      );
    }

    return (
      <Stack gap={4}>
        {item.durationSeconds && (
          <Group gap="xs">
            <AppIcon iconName="Clock" size={12} />
            <Text size="xs">
              Duration: {formatDuration(item.durationSeconds)}
            </Text>
          </Group>
        )}

        {(item.startDatetime || item.endDatetime) && (
          <Group gap="xs">
            <AppIcon iconName="Calendar" size={12} />
            <Text size="xs">
              {item.startDatetime && formatDate(item.startDatetime)}
              {item.startDatetime && item.endDatetime && " - "}
              {item.endDatetime && formatDate(item.endDatetime)}
            </Text>
          </Group>
        )}

        {(item.startTime || item.endTime) && (
          <Group gap="xs">
            <AppIcon iconName="Clock" size={12} />
            <Text size="xs">
              {item.startTime && formatTimeOnly(item.startTime)}
              {item.startTime && item.endTime && " - "}
              {item.endTime && formatTimeOnly(item.endTime)}
            </Text>
          </Group>
        )}

        {item.recurrencePattern && item.recurrencePattern !== "NONE" && (
          <Group gap="xs">
            <AppIcon iconName="Repeat" size={12} />
            <Text size="xs">
              {item.recurrencePattern}
              {item.recurrenceDays &&
                item.recurrencePattern == "WEEKLY" &&
                ` (${formatDaysOfWeek(item.recurrenceDays)})`}
              {item.recurrenceDays &&
                item.recurrencePattern == "MONTHLY" &&
                ` (${item.recurrenceDays})`}
            </Text>
          </Group>
        )}
      </Stack>
    );
  };

  // Render content block cell
  const renderContentBlock = (item: PlayListItem) => (
    <Stack gap={4}>
      <Group gap="xs">
        <Text fw={500} size="sm">
          {item.contentBlock?.name || "Unknown Content Block"}
        </Text>
        {hasOverrides(item) && (
          <Badge size="xs" color="blue" variant="light">
            Overridden
          </Badge>
        )}
      </Group>
      <ContentTypeBadge type={item.contentBlock?.contentType as any} />
    </Stack>
  );

  // Render actions cell
  const renderActions = (item: PlayListItem) => (
    <RecordActions
      context={item}
      onEditClick={onEditPlayListItem}
      onRemoveClick={onRemovePlayListItem}
      onDuplicateClick={onDuplicatePlayListItem}
      removeInProgress={
        removingPlayListItemId === item.id || reorderingItemId === item.id
      }
    />
  );

  // If drag and drop is NOT enabled, use regular AppTable
  if (!isDragDropEnabled) {
    const columns: AppTableColumn<PlayListItem>[] = [
      {
        key: "contentBlock",
        label: "Content Block",
        sortable: false,
        flex: true,
        minWidth: 250,
        render: (_, record) => renderContentBlock(record),
      },
      {
        key: "overrides",
        label: "Overrides",
        sortable: false,
        width: 250,
        minWidth: 200,
        render: (_, record) => renderOverrides(record),
      },
      {
        key: "status",
        label: "Status",
        sortable: true,
        width: 120,
        minWidth: 120,
        align: "left",
        render: (value) => <StatusBadge status={value} />,
      },
      {
        key: "updatedAt",
        label: "Updated",
        sortable: true,
        width: 120,
        minWidth: 120,
        render: (_, record) => (
          <Text size="sm" c="dimmed">
            {formatDateTime(record.createdAt, "datetime")}
          </Text>
        ),
      },
      {
        key: "actions",
        label: "",
        width: 180,
        minWidth: 180,
        align: "left",
        render: (_, record) => renderActions(record),
      },
    ];

    return (
      <AppTable
        data={displayItems}
        hideOnNoData={true}
        columns={columns}
        loading={loading}
        hasMore={hasMore}
        loadingMore={loadingMore}
        onLoadMore={onLoadMore}
        sortBy={sortBy}
        onSortChange={onSortChange}
        multiSort={false}
        getRowKey={(item) => item?.id || ""}
        withBorder
        highlightOnHover
        size={size}
      />
    );
  }

  // Custom table with drag and drop
  if (loading && displayItems.length === 0) {
    return (
      <Paper withBorder>
        <Center p="xl">
          <Stack align="center" gap="md">
            <LoadingIndicator size="lg" />
            <Text c="dimmed">Loading data...</Text>
          </Stack>
        </Center>
      </Paper>
    );
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Paper withBorder>
        <ScrollArea>
          <Table highlightOnHover style={{ width: "100%" }}>
            <Table.Thead>
              <Table.Tr
                style={{
                  background: "var(--mantine-primary-color-light)",
                  color: "var(--mantine-primary-color-light-color)",
                }}
              >
                <Table.Th style={{ width: 40, minWidth: 40 }}>
                  <Text fw={500}></Text>
                </Table.Th>
                <Table.Th style={{ minWidth: 250 }}>
                  <Text fw={500}>Content Block</Text>
                </Table.Th>
                <Table.Th style={{ width: 250, minWidth: 200 }}>
                  <Text fw={500}>Overrides</Text>
                </Table.Th>
                <Table.Th style={{ width: 120, minWidth: 120 }}>
                  <Text fw={500}>Status</Text>
                </Table.Th>
                <Table.Th style={{ width: 120, minWidth: 120 }}>
                  <Text fw={500}>Creatd At</Text>
                </Table.Th>
                <Table.Th style={{ width: 180, minWidth: 180 }}>
                  <Text fw={500}></Text>
                </Table.Th>
              </Table.Tr>
            </Table.Thead>

            <Droppable droppableId="playlist-items">
              {(provided) => (
                <Table.Tbody
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  {displayItems.map((item, index) => (
                    <Draggable
                      key={item.id || `item-${index}`}
                      draggableId={item.id || `item-${index}`}
                      index={index}
                      isDragDisabled={!!reorderingItemId || loading}
                    >
                      {(provided, snapshot) => (
                        <Table.Tr
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          style={{
                            ...provided.draggableProps.style,
                            backgroundColor: snapshot.isDragging
                              ? "var(--mantine-color-blue-0)"
                              : reorderingItemId === item.id
                              ? "var(--mantine-color-gray-1)"
                              : undefined,
                            opacity: reorderingItemId === item.id ? 0.6 : 1,
                          }}
                        >
                          <Table.Td
                            {...provided.dragHandleProps}
                            style={{
                              cursor:
                                reorderingItemId === item.id ? "wait" : "grab",
                              width: 40,
                              minWidth: 40,
                            }}
                          >
                            <AppIcon
                              iconName="GripVertical"
                              size={16}
                              color={
                                reorderingItemId === item.id
                                  ? "var(--mantine-color-gray-5)"
                                  : "var(--mantine-color-gray-7)"
                              }
                            />
                          </Table.Td>
                          <Table.Td style={{ minWidth: 250 }}>
                            {renderContentBlock(item)}
                          </Table.Td>
                          <Table.Td style={{ width: 250, minWidth: 200 }}>
                            {renderOverrides(item)}
                          </Table.Td>
                          <Table.Td style={{ width: 120, minWidth: 120 }}>
                            <StatusBadge status={item.status} />
                          </Table.Td>
                          <Table.Td style={{ width: 120, minWidth: 120 }}>
                            <Text size="sm" c="dimmed">
                              {formatDateTime(item.createdAt, "datetime")}
                            </Text>
                          </Table.Td>
                          <Table.Td style={{ width: 180, minWidth: 180 }}>
                            {renderActions(item)}
                          </Table.Td>
                        </Table.Tr>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </Table.Tbody>
              )}
            </Droppable>
          </Table>
        </ScrollArea>
      </Paper>
    </DragDropContext>
  );
};
